/**
 * URL Utility Functions
 * Provides consistent URL generation for API and file resources
 */

/**
 * Get the base URL for API/file requests
 * In development, the proxy handles routing, so we use relative URLs
 * In production, REACT_APP_API_URL can be set if the API is on a different domain
 */
export const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || '';
};

/**
 * Convert a relative file URL to a full URL
 * Files are stored in wwwroot/Files and served via UseStaticFiles()
 * In development, the proxy forwards /Files requests to the backend
 * 
 * @param {string|null|undefined} relativeUrl - The relative URL (e.g., "/Files/abc-123.jpg")
 * @returns {string|null} The full URL or null if no URL provided
 * 
 * @example
 * getFileUrl("/Files/image.jpg") => "/Files/image.jpg" (or "https://api.example.com/Files/image.jpg" in production)
 * getFileUrl(null) => null
 */
export const getFileUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  
  const baseUrl = getApiBaseUrl();
  
  // In development (no REACT_APP_API_URL), relative URLs work via proxy
  if (!baseUrl) {
    return relativeUrl;
  }
  
  // In production, prepend the API base URL
  // Ensure no double slashes
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanPath = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  
  return `${cleanBase}${cleanPath}`;
};

/**
 * Get profile image URL for a user
 * 
 * @param {object} user - User object with profileImageUrl property
 * @returns {string|null} The full profile image URL or null
 */
export const getProfileImageUrl = (user) => {
  return getFileUrl(user?.profileImageUrl);
};

/**
 * Build API endpoint URL
 * 
 * @param {string} endpoint - The API endpoint (e.g., "/api/auth/me")
 * @returns {string} The full API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  
  if (!baseUrl) {
    return endpoint;
  }
  
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  return `${cleanBase}${cleanPath}`;
};

export default {
  getApiBaseUrl,
  getFileUrl,
  getProfileImageUrl,
  getApiUrl
};
